/*
Main session management module. Uses JWT to handle storing user sessions and
provides convenience methods to get user info and check permissions.
*/
import axios from 'axios'
import JWT from 'jwt-client'
import {includes} from 'lodash'

const API_HOST = process.env.REACT_APP_API_URL || 'http://localhost:3000'
const API_NAMESPACE = process.env.REACT_APP_API_NAMESPACE || '/api/v1'
const BASEURL = `${API_HOST}${API_NAMESPACE}`
const AUTH_ENDPOINT = `${BASEURL}/auth`

// Create a configured axios instance
const server = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/vnd.api+json'
  }
})


/*
* PUBLIC METHODS
*/

/* login(email, password):
Receives email/password and checks against the auth API endpoint.
Creates a new session if successful *and* triggers then() with complete session
data.
Returns: Promise (login is the only async method) */
const login = (email, password) => {
  logout() // Start with a clean slate
  const body = {
    auth: {
      email: email,
      password: password
    }
  }
  return new Promise((resolve, reject) => {
    server.post(AUTH_ENDPOINT, body)
    .then((response) => {
      const session = JWT.read(response.data.jwt)
      if (JWT.validate(session)) {
        JWT.keep(session)
        resolve(session)
      }
      else {
        reject({error: {message: "Invalid user session!"}})
      }
    })
    .catch((error) => reject(error))
  })
}

// logout(): Clears session data
const logout = () => JWT.forget()

// session(): Returns current session data
const session = () => JWT.remember()

// token(): Returns current session token (authenticates API requests)
const token = () => JWT.get()

// loggedIn(): Returns true if session exists and is valid (not expired)
const loggedIn = () => (Boolean(session()) && JWT.validate(session()))

// user(): Returns logged in user data or null
const user = () => (loggedIn() ? session().claim : null)

// hasRole(role): Returns true if logged in user has a particular role
const hasRole = (role) => (loggedIn() ? includes(user().roles, role) : false)

// isAdmin(): Returns true if logged in user has admin role
const isAdmin = () => (loggedIn() ? hasRole('admin') : false)

const auth = {
  login,
  logout,
  session,
  token,
  loggedIn,
  user,
  hasRole,
  isAdmin
}

export default auth



// WEBPACK FOOTER //
// ./src/stores/auth.js